// Core
import { ref, onMounted, onUnmounted } from "vue";

// Libraries
import { useIntervalFn } from "@vueuse/core";

// Composables
import { useCustomToast } from "@/composables/useCustomToast";

// Services
import { sentryService } from "@/core/services/sentry/sentryService";

// Components
import UpdateToast from "@/components/toast/UpdateToast.vue";

// Types
import type { ToastOptions } from "vue-toastification/dist/types/types";
import { POSITION } from "vue-toastification";

export const usePWAUpdate = () => {
  const updateAvailable = ref(false);
  const registration = ref<ServiceWorkerRegistration | null>(null);
  
  const updateToast = useCustomToast(UpdateToast);

  // Cleanup functions
  const cleanupFunctions: (() => void)[] = [];
  
  // Flag to prevent multiple initializations
  const isInitialized = ref(false);

  const updateSW = async () => {
    try {
      if (!registration.value || !registration.value.waiting) return;
      
      // Tell the waiting SW to skip waiting and become the active SW
      registration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload the page to use the new SW
      window.location.reload();
    } catch (error) {
      console.error("Error updating service worker:", error);
      sentryService.captureError(error, "pwa-update-failed", {
        title: "PWA Update Failed",
        message: "Failed to update service worker"
      });
    }
  };

  const showUpdateToast = () => {
    const toastOptions: ToastOptions = {
      timeout: false, // Don't auto-dismiss
      closeOnClick: false,
      draggable: false,
      position: POSITION.TOP_CENTER
    };

    updateToast.info(
      {
        title: "Nova versão disponível! 🚀",
        message: "Uma nova versão do aplicativo está disponível com melhorias e correções. Atualize para obter a melhor experiência.",
        updateAction: updateSW,
        laterAction: () => {
          // Just close the toast, the update will be available next time
          console.log("Update postponed by user");
        }
      },
      toastOptions
    );
  };

  const initPWAUpdate = async () => {
    // Prevent multiple initializations
    if (isInitialized.value) {
      console.warn("PWA update already initialized, skipping duplicate initialization");
      return;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      console.log("Service Workers not supported");
      return;
    }

    try {
      isInitialized.value = true;
      // Check if there's already a registration
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      
      if (existingRegistration) {
        registration.value = existingRegistration;
        
        // Check if there's an update waiting
        if (existingRegistration.waiting) {
          updateAvailable.value = true;
          showUpdateToast();
          return;
        }

        // Listen for updates
        const updateFoundHandler = () => {
          const newWorker = existingRegistration.installing;
          
          if (newWorker) {
            const stateChangeHandler = () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                updateAvailable.value = true;
                showUpdateToast();
              }
            };
            newWorker.addEventListener('statechange', stateChangeHandler);
            
            // Add cleanup for statechange listener
            cleanupFunctions.push(() => {
              newWorker.removeEventListener('statechange', stateChangeHandler);
            });
          }
        };
        
        existingRegistration.addEventListener('updatefound', updateFoundHandler);
        cleanupFunctions.push(() => {
          existingRegistration.removeEventListener('updatefound', updateFoundHandler);
        });
      }

      // Listen for messages from the service worker
      const messageHandler = (event: MessageEvent) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          updateAvailable.value = true;
          showUpdateToast();
        }
      };
      
      navigator.serviceWorker.addEventListener('message', messageHandler);
      cleanupFunctions.push(() => {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
      });

      // Check for updates periodically (every 60 seconds when page is active)
      const checkForUpdates = async () => {
        try {
          if (document.visibilityState === 'visible' && registration.value) {
            await registration.value.update();
          }
        } catch (error) {
          console.error("Error checking for updates:", error);
          sentryService.captureError(error, "pwa-update-check-failed", {
            title: "PWA Update Check Failed",
            message: "Failed to check for service worker updates"
          });
        }
      };

      // Check for updates when page becomes visible
      const visibilityChangeHandler = () => {
        checkForUpdates();
      };
      
      document.addEventListener('visibilitychange', visibilityChangeHandler);
      cleanupFunctions.push(() => {
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
      });
      
      // Initial check
      const initialCheckTimeout = setTimeout(checkForUpdates, 5000);
      cleanupFunctions.push(() => {
        clearTimeout(initialCheckTimeout);
      });
      
      // Periodic checks every 60 seconds when page is active
      const { pause: pauseInterval } = useIntervalFn(() => {
        if (document.visibilityState === 'visible' && registration.value) {
          checkForUpdates();
        }
      }, 60000);
      
      cleanupFunctions.push(() => {
        pauseInterval();
      });
      
    } catch (error) {
      console.error("Error initializing PWA update:", error);
      sentryService.captureError(error, "pwa-init-failed", {
        title: "PWA Initialization Failed",
        message: "Failed to initialize PWA update detection"
      });
      
      // Reset initialization flag on error to allow retry
      isInitialized.value = false;
    }
  };

  // Manual cleanup function for app-level usage
  const cleanup = () => {
    cleanupFunctions.forEach(cleanup => cleanup());
    cleanupFunctions.length = 0; // Clear the array
    isInitialized.value = false; // Allow re-initialization if needed
  };

  onMounted(() => {
    // Initialize PWA update detection after component mounts
    setTimeout(initPWAUpdate, 1000);
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    updateAvailable,
    updateSW,
    showUpdateToast,
    initPWAUpdate,
    cleanup
  };
};